import React from 'react';
import { Box, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import ArrowUpwardRounded from '@mui/icons-material/ArrowUpwardRounded';
import ArrowDownwardRounded from '@mui/icons-material/ArrowDownwardRounded';

/**
 * Shared metric-card look used by Pulse (activity/ratio/unit-economics/
 * fresh-leads/gap-cost tiles) and now Plan's headline scorecard -- one
 * component so "every metric card on this feature looks the same" is
 * enforced by reuse, not by eye. Originally written inline in
 * PulsePage.tsx; pulled out here once Plan needed the same shape.
 */

// Tiny inline trend line for the handful of metrics that actually have a
// real multi-week series behind them (see MetricTile's sparklineValues
// prop docs) -- deliberately plain SVG, not EChartsRenderer, since a
// 96x32px decoration doesn't need a full chart engine's axes/tooltips/
// legend machinery.
export function MiniSparkline({ values, color }: { values: number[]; color: string }) {
  const width = 96;
  const height = 32;
  if (values.length < 2) return null;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;
  const stepX = width / (values.length - 1);
  const points = values.map((v, i) => {
    const x = i * stepX;
    const y = height - ((v - min) / range) * (height - 4) - 2;
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  });
  const linePath = `M${points.join(' L')}`;
  const areaPath = `${linePath} L${width},${height} L0,${height} Z`;
  const gradientId = `spark-${color.replace('#', '')}`;
  return (
    <Box sx={{ width, height, mt: 0.5 }}>
      <svg width="100%" height="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.25} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaPath} fill={`url(#${gradientId})`} stroke="none" />
        <path d={linePath} fill="none" stroke={color} strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Box>
  );
}

export function MetricTile({
  icon: Icon,
  label,
  value,
  delta,
  deltaGood,
  deltaDirection,
  // Formatted previous-period value shown in parens next to the delta,
  // e.g. delta="+6%" + deltaBaselineLabel="89.95%" renders "+6% (89.95%)".
  // Omit when there's no clean single baseline number to show.
  deltaBaselineLabel,
  // A real multi-week series for the handful of metrics that have one.
  // Deliberately NOT wired up for every card: a "sparkline" drawn
  // through two points would be a straight line pretending to be a
  // trend -- worse than no sparkline at all.
  sparklineValues,
  sparklineColor,
  // A neutral qualifier pinned under the number -- the terms it was
  // measured on, not a comparison. Renders whether or not there is a
  // delta, unlike deltaBaselineLabel, which is part of the delta pill and
  // silently disappears when no delta is passed.
  badge,
  tooltip,
  unavailableNote,
}: {
  icon?: React.ElementType;
  label: string;
  value: string;
  delta?: string;
  deltaGood?: boolean | null;
  // Which way the underlying number actually moved -- independent of
  // deltaGood, since "down" isn't always bad (e.g. cost/dial falling).
  // Omit to hide the arrow.
  deltaDirection?: 'up' | 'down' | null;
  deltaBaselineLabel?: string;
  sparklineValues?: number[];
  sparklineColor?: string;
  badge?: string;
  tooltip?: string;
  unavailableNote?: string | null;
}) {
  const theme = useTheme();
  const isUnavailable = unavailableNote != null;
  const hasDeltaColor = deltaGood !== undefined && deltaGood !== null;
  const deltaColor = !hasDeltaColor
    ? theme.palette.text.secondary
    : deltaGood
      ? theme.palette.success.main
      : theme.palette.error.main;
  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        p: 2,
        height: '100%',
        minHeight: 136,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        bgcolor: t.tokens.surface.raised,
        transition: 'box-shadow 120ms ease, border-color 120ms ease',
        '&:hover': {
          boxShadow: `0 2px 10px ${alpha(t.palette.common.black, 0.06)}`,
          borderColor: t.tokens.border.default,
        },
      })}
    >
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1} sx={{ mb: 1 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ minWidth: 0 }}>
          {Icon ? (
            <Box
              sx={(t) => ({
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: `${t.ds.radius.sm}px`,
                bgcolor: alpha(t.palette.primary.main, 0.1),
                color: t.palette.primary.main,
                flexShrink: 0,
              })}
            >
              <Icon sx={{ fontSize: 16 }} />
            </Box>
          ) : null}
          {/* No noWrap here on purpose -- these labels can run long
           * ("Forecast vs. scheduled capacity") and a 220px-min tile is
           * too narrow to fit that on one line. Letting it wrap to 2
           * lines beats silently truncating a label the tooltip's own
           * heading (below) then has to repeat in full anyway. */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', lineHeight: 1.3 }}
          >
            {label}
          </Typography>
        </Stack>
        {tooltip ? (
          <Tooltip
            title={
              <Box sx={{ maxWidth: 280 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  {label}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.45 }}>
                  {tooltip}
                </Typography>
              </Box>
            }
            arrow
            placement="top"
          >
            <InfoOutlined sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0, mt: '2px' }} />
          </Tooltip>
        ) : null}
      </Stack>
      {isUnavailable ? (
        <>
          <Typography variant="h2" sx={{ fontWeight: 700, color: 'text.disabled', fontVariantNumeric: 'tabular-nums' }}>
            &ndash;
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, lineHeight: 1.4 }}>
            {unavailableNote}
          </Typography>
        </>
      ) : (
        <>
          <Typography variant="h2" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {value}
          </Typography>
          {sparklineValues && sparklineValues.length >= 2 ? (
            <MiniSparkline values={sparklineValues} color={sparklineColor ?? deltaColor} />
          ) : null}
          <Box sx={{ mt: 'auto', pt: 0.75 }}>
            {delta ? (
              // `noWrap` below means long delta text (e.g. "45747 attempts
              // needed this wk") silently clips with a "..." and no way to
              // read the rest -- wrapping the whole pill in a Tooltip with
              // the same full text gives that back on hover, same pattern
              // as the label/icon tooltip above.
              <Tooltip title={`${delta}${deltaBaselineLabel ? ` (${deltaBaselineLabel})` : ''}`} arrow placement="top">
                <Box
                  component="span"
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    maxWidth: '100%',
                    gap: 0.25,
                    px: 0.75,
                    py: 0.25,
                    borderRadius: 999,
                    bgcolor: hasDeltaColor ? alpha(deltaColor, 0.12) : alpha(theme.palette.text.secondary, 0.08),
                    cursor: 'default',
                  }}
                >
                  {deltaDirection ? (
                    deltaDirection === 'up' ? (
                      <ArrowUpwardRounded sx={{ fontSize: 12, color: deltaColor, flexShrink: 0 }} />
                    ) : (
                      <ArrowDownwardRounded sx={{ fontSize: 12, color: deltaColor, flexShrink: 0 }} />
                    )
                  ) : null}
                  <Typography
                    variant="caption"
                    noWrap
                    sx={{ fontVariantNumeric: 'tabular-nums', color: deltaColor, fontWeight: hasDeltaColor ? 600 : 500 }}
                  >
                    {delta}
                    {deltaBaselineLabel ? ` (${deltaBaselineLabel})` : ''}
                  </Typography>
                </Box>
              </Tooltip>
            ) : null}
          </Box>
          {badge ? (
            // A footer strip rather than another pill beside the delta:
            // the badge is a condition the whole tile was measured under,
            // not a second statistic, and two pills side by side read as
            // two comparable facts. Bleeds to the card's edges so the rule
            // spans it properly.
            <Box
              sx={(t) => ({
                mx: -2,
                mt: 1.25,
                mb: -0.5,
                px: 2,
                pt: 1,
                borderTop: `1px solid ${t.tokens.border.subtle}`,
              })}
            >
              <Typography variant="caption" noWrap sx={{ color: 'text.secondary', display: 'block' }}>
                {badge}
              </Typography>
            </Box>
          ) : null}
        </>
      )}
    </Paper>
  );
}

export default MetricTile;
